import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';
import { LogsFolderInfo, UploadLogsDirectories } from '../../Interfaces/LogsInfo/LogsFolderInfo';
export enum LogsActionTypes {
  REQUEST_LOGS_FOLDER_PATH = 'REQUEST_LOGS_FOLDER_PATH',
  RECEIVED_LOGS_FOLDER_PATH = 'RECEIVED_LOGS_FOLDER_PATH',
  REQUEST_UPLOAD_LOGS_DIRECTORIES = 'REQUEST_UPLOAD_LOGS_DIRECTORIES',
  RECEIVED_UPLOAD_LOGS_DIRECTORIES = 'RECEIVED_UPLOAD_LOGS_DIRECTORIES',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface LogsState {
  isFetchingLogsFolderInfo: boolean;
  folderInfo: LogsFolderInfo;
  isFetchingUploadLogsDirs: boolean;
  uploadLogsDirectories: UploadLogsDirectories;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface RequestLogsFolderInfo {
  type: LogsActionTypes.REQUEST_LOGS_FOLDER_PATH;
  isFetching: boolean;
}

interface ReceivedLogsFolderInfo {
  type: LogsActionTypes.RECEIVED_LOGS_FOLDER_PATH;
  folderInfo: LogsFolderInfo;
}

interface RequestUploadLogsDirectories {
  type: LogsActionTypes.REQUEST_UPLOAD_LOGS_DIRECTORIES;
  isFetching: boolean;
}

interface ReceivedUploadLogsDirectories {
  type: LogsActionTypes.RECEIVED_UPLOAD_LOGS_DIRECTORIES;
  uploadLogsDirectories: UploadLogsDirectories;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = RequestLogsFolderInfo | ReceivedLogsFolderInfo | RequestUploadLogsDirectories | ReceivedUploadLogsDirectories;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;

  return false;
};

export const actionCreators = {
  fetchLogsFolderInfo:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${LogsActionTypes.REQUEST_LOGS_FOLDER_PATH}`);

      dispatch({
        type: LogsActionTypes.REQUEST_LOGS_FOLDER_PATH,
        isFetching: true,
      });

      const response = await fetch(
        `logs/GetLogsFolderInfo`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as LogsFolderInfo;

      console.log(`Dispatching ${LogsActionTypes.RECEIVED_LOGS_FOLDER_PATH}`);

      dispatch({
        type: LogsActionTypes.RECEIVED_LOGS_FOLDER_PATH,
        folderInfo: result,
      });
    },

    fetchUploadLogsDirectories:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${LogsActionTypes.REQUEST_UPLOAD_LOGS_DIRECTORIES}`);

      dispatch({
        type: LogsActionTypes.REQUEST_UPLOAD_LOGS_DIRECTORIES,
        isFetching: true,
      });

      const response = await fetch(
        `logs/GetUploadLogsDirectories`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as UploadLogsDirectories;
      
      console.log(`Dispatching ${LogsActionTypes.RECEIVED_UPLOAD_LOGS_DIRECTORIES}`);

      dispatch({
        type: LogsActionTypes.RECEIVED_UPLOAD_LOGS_DIRECTORIES,
        uploadLogsDirectories: result,
      });
    },
};

const initialLogsFolderInfo: LogsFolderInfo = {
  Path: '',
};

const initialUploadLogsDirectories: UploadLogsDirectories = {
  DirectoryNames: [],
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: LogsState = {
  isFetchingLogsFolderInfo: false,
  folderInfo: initialLogsFolderInfo,
  isFetchingUploadLogsDirs: false,
  uploadLogsDirectories: initialUploadLogsDirectories,
};

export const reducer: Reducer<LogsState> = (
  state: LogsState | undefined,
  incomingAction: Action
): LogsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case LogsActionTypes.REQUEST_LOGS_FOLDER_PATH:
      return {
        ...state,
        isFetchingLogsFolderInfo: action.isFetching,
      };
    case LogsActionTypes.RECEIVED_LOGS_FOLDER_PATH:
      return {
        ...state,
        folderInfo: action.folderInfo,
        isFetchingLogsFolderInfo: false,
      };
    case LogsActionTypes.REQUEST_UPLOAD_LOGS_DIRECTORIES:
      return {
        ...state,
        isFetchingUploadLogsDirs: action.isFetching,
      };
    case LogsActionTypes.RECEIVED_UPLOAD_LOGS_DIRECTORIES:
      return {
        ...state,
        uploadLogsDirectories: action.uploadLogsDirectories,
        isFetchingUploadLogsDirs: false,
      };

    default:
      break;
  }

  return state;
};
